/**
 * Date utility functions for API date filtering.
 * These functions return ISO-like date strings in local timezone for backend Ransack queries.
 */

/**
 * Format a date as ISO string in local timezone (YYYY-MM-DDTHH:mm:ss)
 */
const formatLocalDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Get start of day (00:00:00) for a given date
 */
export const getStartOfDay = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

/**
 * Get end of day (23:59:59) for a given date
 */
export const getEndOfDay = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
};

/**
 * Get start of week (Sunday 00:00:00) for a given date
 */
export const getStartOfWeek = (date: Date = new Date()): Date => {
  const dayOfWeek = date.getDay();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - dayOfWeek, 0, 0, 0, 0);
};

/**
 * Get end of week (Saturday 23:59:59) for a given date
 */
export const getEndOfWeek = (date: Date = new Date()): Date => {
  const dayOfWeek = date.getDay();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (6 - dayOfWeek), 23, 59, 59, 999);
};

/**
 * Get start of month (1st day, 00:00:00) for a given date
 */
export const getStartOfMonth = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
};

/**
 * Get end of month (last day, 23:59:59) for a given date
 */
export const getEndOfMonth = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
};

export interface IDateRange {
  start: string;
  end: string;
}

export const parseDateInputValue = (value: string): Date | null => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

export const toApiDateRangeQuery = (from: Date | null, to: Date | null): Partial<IDateRange> => {
  const range: Partial<IDateRange> = {};

  if (from) {
    range.start = formatLocalDateTime(getStartOfDay(from));
  }
  if (to) {
    range.end = formatLocalDateTime(getEndOfDay(to));
  }

  return range;
};

export type DateFilterOption = 'all' | 'today' | 'week' | 'month';

/**
 * Get date range for a filter option (today, week, month)
 * Returns ISO-formatted strings in local timezone
 */
export const getDateRangeForFilter = (filter: DateFilterOption): IDateRange | undefined => {
  if (filter === 'all') return undefined;

  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (filter) {
    case 'today':
      startDate = getStartOfDay(now);
      endDate = getEndOfDay(now);
      break;
    case 'week':
      startDate = getStartOfWeek(now);
      endDate = getEndOfWeek(now);
      break;
    case 'month':
      startDate = getStartOfMonth(now);
      endDate = getEndOfMonth(now);
      break;
    default:
      return undefined;
  }

  return {
    start: formatLocalDateTime(startDate),
    end: formatLocalDateTime(endDate),
  };
};

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Format year and optional month into a readable string
 * @param year - The year to format (null returns empty string)
 * @param month - Optional month (1-12, null omits month from output)
 * @returns Formatted string like "January 2020" or "2020" or ""
 */
export const formatYearMonth = (year: number | null, month: number | null): string => {
  if (!year) return '';
  if (month) {
    return `${MONTH_NAMES[month - 1]} ${year}`;
  }
  return `${year}`;
};

/**
 * Parse birthday string in format "YYYY" or "YYYY-MM"
 * @param birthdayString - String in format "YYYY" or "YYYY-MM"
 * @returns Object with year and month (month is null if not provided)
 */
export const parseBirthdayString = (birthdayString: string | null): { year: number | null; month: number | null } => {
  if (!birthdayString) return { year: null, month: null };

  const parts = birthdayString.split('-');
  const year = parseInt(parts[0], 10);
  const month = parts[1] ? parseInt(parts[1], 10) : null;

  return { year, month };
};

/**
 * Format birthday string for display
 * @param birthdayString - String in format "YYYY" or "YYYY-MM"
 * @returns Formatted string like "January 1985" or "1985"
 */
export const formatBirthdayString = (birthdayString: string | null): string => {
  if (!birthdayString) return '';

  const { year, month } = parseBirthdayString(birthdayString);
  return formatYearMonth(year, month);
};

/**
 * Typical work interval in years (used for calculating year ranges)
 */
export const WORK_INTERVAL_YEARS = 100;

/**
 * Generate array of years from current year down to (current year - WORK_INTERVAL_YEARS)
 * Used for year dropdowns in work history, education, and board forms
 * @returns Array of years in descending order
 */
export const generateYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - WORK_INTERVAL_YEARS;
  const years: number[] = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Calendar days from today to `endsOn` (inclusive of end day). Returns null if already expired. */
export const calendarDaysUntilDate = (endsOn: string): number | null => {
  const start = new Date();
  const end = new Date(endsOn);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (end < start) return null;
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
};

/**
 * Array of months with numeric values (1-12) and labels
 * Used for month dropdowns in work history, education, and board forms
 */
export const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];
